<template>
  <div class="stacked-bar">
    <div class="header">
      <h3>Categories</h3>
      <div class="toggle">
        <button :class="{ active: dataType === 'sales' }" @click="setDataType('sales')">Sales</button>
        <button :class="{ active: dataType === 'count' }" @click="setDataType('count')">Count</button>
      </div>
    </div>
    <div v-if="hasData">
      <div class="chart-wrapper">
        <vue3-chart-js
          v-if="renderComponent"
          :id="barChart.id"
          :type="barChart.type"
          :data="barChart.data"
          :options="barChart.options"
        />
      </div>
      <div class="stacked-list">
        <details v-for="(products, category) in stackedProducts" :key="category" class="category-item">
          <summary class="summary-row">
            <span class="cat-name">{{ category }}</span>
            <span v-if="dataType === 'count'" class="cat-value">{{ categoryTotals[category] }}</span>
            <CurrencyFormatter v-else class="cat-value" :value="categoryTotals[category]" :currency="currency" />
          </summary>
          <table class="inner-table">
            <tbody>
              <tr v-for="(val, prod) in products" :key="prod">
                <td>{{ prod }}</td>
                <td v-if="dataType === 'count'">{{ val }}</td>
                <td v-else><CurrencyFormatter :value="val" :currency="currency" /></td>
              </tr>
            </tbody>
          </table>
        </details>
        <div class="total-row">
          <span>Total</span>
          <span v-if="dataType === 'count'">{{ overallTotal }}</span>
          <CurrencyFormatter v-else :value="overallTotal" :currency="currency" />
        </div>
      </div>
    </div>
    <p v-else class="no-data">No data for today</p>
  </div>
</template>

<script>
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";
import { reports } from "@monolith/helpers";
import { mapGetters } from "vuex";
import { CurrencyFormatter } from "@monolith/components";

const COLORS = ["#ffbe0b", "#219ebc", "#fb5607", "#f94144", "#f3722c", "#f8961e", "#f9844a", "#f9c74f", "#90be6d", "#43aa8b", "#4d908e", "#577590", "#277da1"];

export default {
  name: "StackedBarChart",
  components: { Vue3ChartJs, CurrencyFormatter },
  data() {
    return {
      renderComponent: true,
      hasData: false,
      dataType: "sales",
      categoryTotals: {},
      stackedProducts: {},
      overallTotal: 0,
      barChart: {
        id: "stacked-bar",
        type: "bar",
        data: { labels: [], datasets: [] },
        options: {
          scales: {
            x: { stacked: true },
            y: { stacked: true },
          },
          plugins: {
            legend: { display: true },
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({ currency: "currency" }),
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async setDataType(type) {
      this.dataType = type;
      await this.loadData();
    },
    getTimeZoneOffset() {
      return new Date().getTimezoneOffset() / -60;
    },
    getHoursWithData(data) {
      const hours = [];
      const offset = this.getTimeZoneOffset();
      data.forEach((d) => {
        let h = parseInt(d.key[0].substring(11, 13)) + offset;
        if (h >= 24) h -= 24;
        if (!hours.includes(h)) hours.push(h);
      });
      return hours.sort((a, b) => a - b);
    },
    buildDatasets(data) {
      const hours = this.getHoursWithData(data);
      const offset = this.getTimeZoneOffset();
      const seriesMap = {};

      data.forEach((d) => {
        let hour = parseInt(d.key[0].substring(11, 13)) + offset;
        if (hour >= 24) hour -= 24;
        const cat = d.key[1];

        if (!seriesMap[cat]) {
          seriesMap[cat] = {};
          hours.forEach((h) => { seriesMap[cat][h] = 0; });
        }
        seriesMap[cat][hour] = (seriesMap[cat][hour] || 0) + d.value;
      });

      const datasets = Object.entries(seriesMap).map(([label, vals], i) => ({
        label,
        data: hours.map((h) => parseFloat((vals[h] || 0).toFixed(2))),
        backgroundColor: COLORS[i % COLORS.length],
      }));

      return { labels: hours, datasets };
    },
    buildStackedProducts(productData) {
      const grouped = {};
      productData.forEach((item) => {
        const category = item.key[1];
        const product = item.key[2];
        if (!grouped[category]) grouped[category] = {};
        grouped[category][product] = (grouped[category][product] || 0) + item.value;
      });
      return grouped;
    },
    buildCategoryTotals(stackedProducts) {
      const totals = {};
      Object.entries(stackedProducts).forEach(([cat, products]) => {
        totals[cat] = Object.values(products).reduce((sum, v) => sum + v, 0);
      });
      return totals;
    },
    async loadData() {
      const company = localStorage.getItem("company");
      const site = localStorage.getItem("site");
      const start = new Date();
      start.setHours(0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59);

      const startStr = start.toISOString().substring(0, 13);
      const endStr = end.toISOString().substring(0, 13);

      const [stackedData, productData] = await Promise.all([
        reports.fetchRawStackedData(startStr, endStr, this.dataType, company, site),
        reports.fetchRawStackedProductData(startStr, endStr, this.dataType, company, site),
      ]);

      if (!stackedData || stackedData.length === 0) {
        this.hasData = false;
        return;
      }

      this.hasData = true;
      const { labels, datasets } = this.buildDatasets(stackedData);
      this.stackedProducts = this.buildStackedProducts(productData);
      this.categoryTotals = this.buildCategoryTotals(this.stackedProducts);
      this.overallTotal = Object.values(this.categoryTotals).reduce((sum, v) => sum + v, 0);

      this.barChart.data.labels = labels;
      this.barChart.data.datasets = datasets;
      this.forceRerender();
    },
    forceRerender() {
      this.renderComponent = false;
      this.$nextTick(() => { this.renderComponent = true; });
    },
  },
};
</script>

<style scoped lang="scss">
.stacked-bar {
  padding: 1rem;
  background-color: var(--secondary-colour);
  border-radius: 1rem;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;

    h3 {
      margin: 0;
    }

    .toggle {
      display: flex;
      gap: 0.5rem;

      button {
        padding: 0.3rem 0.8rem;
        border: 1px solid var(--primary-colour, #3bafda);
        border-radius: 0.4rem;
        background: transparent;
        cursor: pointer;
        font-size: 0.9rem;

        &.active {
          background: var(--primary-colour, #3bafda);
          color: white;
        }
      }
    }
  }

  .chart-wrapper {
    width: 100%;
  }

  .stacked-list {
    margin-top: 1rem;

    .category-item {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0.3rem 0;

      .summary-row {
        display: flex;
        justify-content: space-between;
        cursor: pointer;
        padding: 0.3rem 0.5rem;
        list-style: none;

        .cat-name {
          font-weight: 500;
        }
      }

      .inner-table {
        width: 100%;
        margin: 0.3rem 0 0.3rem 1.5rem;

        td {
          padding: 0.15rem 0.5rem;
          width: 200px;
        }
      }
    }

    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 0.4rem 0.5rem;
      font-weight: bold;
      text-decoration: underline;
      margin-top: 0.3rem;
    }
  }

  .no-data {
    color: var(--text-muted, #888);
  }
}
</style>
